import React, { useState, useMemo, useRef } from "react";

/**
 * NormalDistributionDemo (v3)
 * ────────────────────────────────────────────────────────────────────────────
 * Interactive bell-curve visualiser (pure React + SVG).
 *   • Live μ / σ sliders — now *visibly* affect the drawing by default.
 *   • Optionally keep the curve centred (autoFit) or freeze the axis.
 *   • Tick labels under the axis so students can read real numbers.
 *
 * Props
 * ──────
 * mean       number   default 0
 * stdDev     number   default 1  (clamped > 0)
 * autoFit    boolean  default false  — if true, domain recentres on each edit
 * showCurve  boolean  default true
 * width      number   default 400  (px)
 * height     number   default 140  (px)
 */
export default function NormalDistributionDemo({
  mean: initialMean = 0,
  stdDev: initialStd = 1,
  autoFit = false,
  showCurve = true,
  width = 1200,
  height = 440
}) {
  /* ───────── state ───────── */
  const [mean, setMean] = useState(initialMean);
  const [stdDev, setStdDev] = useState(initialStd);

  /* store the startup domain for frozen-axis mode */
  const frozenDomainRef = useRef([
    initialMean - 4 * initialStd,
    initialMean + 4 * initialStd
  ]);

  /* ───────── math ───────── */
  const pdf = (x, mu, sigma) =>
    (1 / (sigma * Math.sqrt(2 * Math.PI))) * Math.exp(-0.5 * ((x - mu) / sigma) ** 2);

  const domain = useMemo(() => {
    return autoFit
      ? [mean - 4 * stdDev, mean + 4 * stdDev]
      : frozenDomainRef.current;
  }, [autoFit, mean, stdDev]);

  /* build SVG path */
  const pathD = useMemo(() => {
    if (!showCurve) return "";

    const [xMin, xMax] = domain;
    const n = 300;
    const densities = new Array(n + 1);

    let maxD = 0;
    for (let i = 0; i <= n; i++) {
      const x = xMin + (i / n) * (xMax - xMin);
      const d = pdf(x, mean, stdDev);
      densities[i] = d;
      if (d > maxD) maxD = d;
    }
    if (maxD === 0) maxD = 1;

    const pts = densities.map((d, i) => {
      const px = (i / n) * width;
      const py = height - (d / maxD) * height;
      return `${px.toFixed(2)},${py.toFixed(2)}`;
    });

    return "M" + pts.join(" L");
  }, [domain, mean, stdDev, width, height, showCurve]);

  /* ───────── handlers ───────── */
  const handleMeanChange = (e) => {
    const v = parseFloat(e.target.value);
    setMean(Number.isFinite(v) ? v : 0);
  };

  const handleStdDevChange = (e) => {
    const v = parseFloat(e.target.value);
    setStdDev(v > 0 ? v : 0.1);
  };

  /* ───────── UI ───────── */
  const inputStyle = {
    width: 60,
    padding: "2px 4px",
    border: "1px solid #ccc",
    borderRadius: 4
  };

  const axisTicks = useMemo(() => {
    const ticks = [];
    for (let k = -3; k <= 3; k++) {
      const xVal = mean + k * stdDev;
      const t = (xVal - domain[0]) / (domain[1] - domain[0]);
      const px = t * width;
      ticks.push({ px, label: xVal.toFixed(0) });
    }
    return ticks;
  }, [domain, mean, stdDev, width]);

  return (
    <div
      role="img"
      aria-label={`Normal distribution (mean ${mean}, standard deviation ${stdDev})`}
      className="flex flex-col gap-2 p-3 border border-gray-300 rounded-lg bg-sky-50/30"
      style={{ maxWidth: width + 40 }}
    >
      {/* Controls */}
      <div className="flex gap-4 items-center text-sm">
        <label>
          μ:
          <input
            className="inputBox"
            type="number"
            step="1"
            value={mean}
            onChange={handleMeanChange}
            style={inputStyle}
          />
        </label>
        <label>
          σ:
          <input
            className="inputBox"
            type="number"
            step="0.1"
            min="0.1"
            value={stdDev}
            onChange={handleStdDevChange}
            style={inputStyle}
          />
        </label>
      </div>

      {/* Curve canvas */}
      <svg
        width={width}
        height={height + 20} /* extra 20 px for tick labels */
        className="block mx-auto select-none"
        style={{ overflow: "visible" }}
      >
        {/* curve */}
        {showCurve && (
          <path
            d={pathD}
            fill="none"
            stroke="#0d6efd"
            strokeWidth={2}
            vectorEffect="non-scaling-stroke"
          />
        )}

        {/* markers at μ and μ ± σ */}
        {[mean, mean - stdDev, mean + stdDev].map((xVal, idx) => {
          const t = (xVal - domain[0]) / (domain[1] - domain[0]);
          const px = t * width;
          const labels = ["μ", "μ-σ", "μ+σ"];
          return (
            <g key={labels[idx]}>
              <line
                x1={px}
                x2={px}
                y1={0}
                y2={height}
                stroke="#8884d8"
                strokeDasharray="4 4"
              />
              <text x={px + 2} y={12} fontSize="30" fill="#555">
                {labels[idx]}
              </text>
            </g>
          );
        })}

        {/* x-axis tick labels */}
        {axisTicks.map(({ px, label }) => (
          <g key={label}>
            <line x1={px} x2={px} y1={height} y2={height + 4} stroke="#555" />
            <text
              x={px}
              y={height + 14}
              fontSize="30"
              textAnchor="middle"
              fill="#555"
            >
              {label}
            </text>
          </g>
        ))}
      </svg>

      <p className="text-xs text-gray-700 mt-1">
        68-95-99.7 rule → ~68 % within ±1σ, 95 % within ±2σ.
      </p>
    </div>
  );
}